import './App.css';
import { CartItemDetails, CategoryDetails, StoreData } from './data/store-data';
import React, { useEffect, useMemo, useState } from 'react';
import { Route, BrowserRouter, Routes } from 'react-router-dom';
import Cart from './views/cart/Cart';
import { CartContext } from './store/CartContext';
import { DemoProvider } from './store/DemoContext';
import {
  CheckoutContext,
  Environments,
  FlowsOptions,
  IErrors,
  PaymentOptions,
  SourceOptions,
} from './store/CheckoutContext';

import CheckoutIframe from './views/checkout/CheckoutIframe';

import CheckoutHostedFields from './views/checkout/CheckoutHostedFields';
import Iframe from './views/checkout/Iframe';
import IfraneDesktop from './views/checkout/IfraneDesktop';

import Confirmation from './views/confirmation/Confirmation';
import Header from './components/header/Header';
import Home from './views/home/Home';
import ItemDetails from './components/item/ItemDetails';
import List from './views/list/List';
import Progress from './views/progress/Progress';
import Reject from './views/reject/Reject';
import Success from './views/success/Success';
import Cancel from './views/cancel/Cancel';
import Demo from './views/demo/Demo';
import { getDemoKeys } from './helpers/checkout-helpers';
import Mit from './views/mit/Mit';
import MitMessage from './views/mit/MitMessage';
import CheckoutSteps from './views/checkout/CheckoutSteps';
import TerminalIframe from './views/checkout/TerminalIframe';

function App() {
  const storeData = useMemo(() => new StoreData(), []);
  const [categories, setCategories] = useState([] as CategoryDetails[]);
  const [cart, setCart] = useState(storeData.getCart());
  const [errors, setErrors] = useState(storeData.getErrors());
  const [paymentErrors, setPaymentErrors] = useState(storeData.getPaymentErrors());
  const [environment, setEnvironment] = useState(storeData.getEnvironment());
  const [loading, setLoading] = useState(storeData.getLoading());
  const [redirect, setRedirect] = useState<string>(storeData.getRedirect());
  const [iframeSize, setIframeSize] = useState<string>(storeData.getIframeSize());
  const [paymentOptions, setPaymentOptions] = useState<PaymentOptions>(PaymentOptions.cit);
  const [sourceOptions, setSourceOptions] = useState<SourceOptions>(SourceOptions.empty);
  const [flow, setFlow] = useState<FlowsOptions>(FlowsOptions.iframe);

  useEffect(() => {
    // @ts-ignore
    if (typeof window?.Cypress === 'undefined') {
      // @ts-ignore
      if (typeof window.top?.Cypress === 'undefined' && window?.top?.location?.search) {
        window?.top?.location?.search
          ?.replace('?', '')
          ?.split('&')
          ?.forEach(el => {
            if (el.includes('mid=')) {
              localStorage.setItem('mid', el.replace('mid=', ''));
            }

            if (el.includes('siteId=')) {
              localStorage.setItem('siteId', el.replace('siteId=', ''));
            }

            if (el.includes('appKey=')) {
              localStorage.setItem('appKey', el.replace('appKey=', ''));
            }

            if (el.includes('appSecret=')) {
              localStorage.setItem('appSecret', el.replace('appSecret=', ''));
            }

            if (el.includes('spreedlyKey=')) {
              localStorage.setItem('spreedlyKey', el.replace('spreedlyKey=', ''));
            }

            if (el.includes('environment=')) {
              const env = el.replace('environment=', '');
              if (Environments[env as Environments]) {
                updateEnvironment(env as Environments);
              }
            }
          });

        // updateEnvironment(process.env.REACT_APP_ENVIRONMENT as Environments);

        updateCart([]);
      }
    }
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (typeof window?.Cypress === 'undefined' && window?.top?.location?.pathname?.includes('demo')) {
      const keys = getDemoKeys();
      localStorage.setItem('mid', keys.mid);
      localStorage.setItem('siteId', keys.siteId);
      localStorage.setItem('appKey', keys.appKey);
      localStorage.setItem('appSecret', keys.appSecret);
      localStorage.setItem('spreedlyKey', keys.spreedlyKey);
      updateEnvironment(process.env.REACT_APP_ENVIRONMENT as Environments);
    }
  }, []);

  useEffect(() => {
    storeData.getCategories().then(data => setCategories(data));
  }, [storeData]);

  function updateCart(cart: CartItemDetails[]) {
    storeData.setCart(cart);
    setCart(cart);
  }

  function updateEnvironment(environment: Environments) {
    storeData.setEnvironment(environment);
    setEnvironment(environment);
  }

  function updateLoading(loading: boolean) {
    storeData.setLoading(loading);
    setLoading(loading);
  }

  function updateErrors(errors: IErrors) {
    storeData.setErrors(errors);
    setErrors(errors);
  }

  function updatePaymentErrors(paymentErrors: IErrors) {
    storeData.setErrors(paymentErrors);
    setPaymentErrors(paymentErrors);
  }

  function updateRedirect(redirect: string) {
    storeData.setRedirect(redirect);
    setRedirect(redirect);
  }

  function updateIframeSize(iframeSize: string) {
    storeData.setIframeSize(iframeSize);
    setIframeSize(iframeSize);
  }

  return (
    <CartContext.Provider value={{ cart, setCart: updateCart }}>
      <CheckoutContext.Provider
        value={{
          environment,
          loading,
          errors,
          paymentErrors,
          redirect,
          iframeSize,
          paymentOptions,
          sourceOptions,
          flow,
          setEnvironment: updateEnvironment,
          setLoading: updateLoading,
          setErrors: updateErrors,
          setPaymentErrors: updatePaymentErrors,
          setRedirect: updateRedirect,
          setIframeSize: updateIframeSize,
          setPaymentOptions: setPaymentOptions,
          setSourceOptions: setSourceOptions,
          setFlow: setFlow,
        }}
      >
        <DemoProvider>
          <BrowserRouter>
            <div className="App">
              <Header />
              <Routes>
                <Route path="/" element={<Home categories={categories} />} />
                <Route path="/list/:listId/:itemId" element={<ItemDetails />} />
                <Route path="/list/:listId" element={<List categories={categories} />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/demo/cart" element={<Cart />} />
                <Route path="/terminal" element={<Iframe />} />
                <Route path="/terminal-iframe" element={<TerminalIframe />} />
                <Route path="/checkout" element={<Iframe />} />
                <Route path="/checkout-iframe" element={<CheckoutIframe />} />
                <Route path="/checkout-steps" element={<CheckoutSteps />} />
                <Route path="/iframe" element={<Iframe />} />
                <Route path="/iframe-desktop" element={<IfraneDesktop />} />
                <Route path="/checkout-hosted-fields" element={<CheckoutHostedFields />} />
                <Route path="/confirm" element={<Confirmation />} />
                <Route path="/decline" element={<Reject />} />
                <Route path="/progress" element={<Progress />} />
                <Route path="/success" element={<Success />} />
                <Route path="/mit-message" element={<MitMessage />} />
                <Route path="/cancel" element={<Cancel />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/demo/checkout-iframe" element={<CheckoutIframe />} />
                <Route path="/demo/iframe/consent" element={<Iframe />} />
                <Route path="/demo/iframe/no-consent" element={<Iframe />} />
                <Route path="/demo/iframe/frictionless" element={<Iframe />} />
                <Route path="/demo/iframe/challenge-success" element={<Iframe />} />
                <Route path="/demo/iframe/challenge-decline" element={<Iframe />} />
                <Route path="/demo/iframe/challenge-frictionless" element={<Iframe />} />
                <Route path="/demo/decline" element={<Reject />} />
                <Route path="/demo/success" element={<Success />} />
                <Route path="/demo/:listId/:itemId" element={<ItemDetails />} />
                <Route path="/demo/:listId" element={<List categories={categories} />} />
                <Route path="/mit" element={<Mit />} />
              </Routes>
            </div>
          </BrowserRouter>
        </DemoProvider>
      </CheckoutContext.Provider>
    </CartContext.Provider>
  );
}

export default App;
